import React, { useState, useEffect, useMemo } from 'react';
import { Concept } from '../../utils/types';
import { motion, AnimatePresence } from 'framer-motion';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import AppIcon from '../../utils/AppIcon';

interface ConceptGridProps {
    concepts: Concept[];
    isLoading: boolean;
    productSelected: boolean;
    isSorting?: boolean;
    zoomConcept: Concept | null;
    setZoomConcept: (concept: Concept | null) => void;
}

const INITIAL_ITEMS = 32;
const ITEMS_PER_LOAD = 16;

const ConceptGrid: React.FC<ConceptGridProps> = ({ concepts, isLoading, productSelected, isSorting = false, zoomConcept, setZoomConcept }) => {
    const [displayedConcepts, setDisplayedConcepts] = useState<Concept[]>([]);
    const [hasMore, setHasMore] = useState(true);
    const [isLoadingMore, setIsLoadingMore] = useState(false);

    useEffect(() => {
        if (concepts.length > 0) {
            setDisplayedConcepts(concepts.slice(0, INITIAL_ITEMS));
            setHasMore(concepts.length > INITIAL_ITEMS);
            setIsLoadingMore(false);
        } else {
            setDisplayedConcepts([]);
            setHasMore(false);
            setIsLoadingMore(false);
        }
    }, [concepts]);

    const loadMore = () => {
        if (!hasMore || isLoadingMore || isLoading || isSorting) return;

        setIsLoadingMore(true);

        setTimeout(() => {
            const currentLength = displayedConcepts.length;
            const nextItems = concepts.slice(currentLength, currentLength + ITEMS_PER_LOAD);

            if (nextItems.length > 0) {
                setDisplayedConcepts(prev => [...prev, ...nextItems]);
                setHasMore(currentLength + nextItems.length < concepts.length);
            } else {
                setHasMore(false);
            }

            setIsLoadingMore(false);
        }, 300);
    };

    const { observerRef } = useInfiniteScroll(loadMore, {
        enabled: hasMore && !isLoading && !isSorting && !isLoadingMore
    });

    const LoadingSkeleton = () => (
        <>
            {Array.from({ length: INITIAL_ITEMS }).map((_, index) => (
                <div key={index} className="aspect-square bg-gray-200 animate-pulse rounded-lg"
                     style={{ animationDelay: `${index * 0.1}s` }}></div>
            ))}
        </>
    );

    const EmptyState = () => (
        <div className="col-span-full flex flex-col items-center justify-center py-10 text-gray-500 h-full">
            {productSelected ? (
                <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-16 w-16 mb-4 text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                    <p className="text-lg">No concept images found for this product</p>
                </>
            ) : (
                <>
                    <img src="/images/frame-concepts.svg" alt=""/>
                    <p className="text-lg">Please select a product image</p>
                </>
            )}
        </div>
    );

    const SortingOverlay = () => (
        <div className="absolute inset-0 bg-white bg-opacity-70 z-10 flex items-center justify-center">
            <div className="flex flex-col items-center space-y-3">
                <svg className="animate-spin h-10 w-10 text-blue-500"
                     xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10"
                            stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
            </div>
        </div>
    );

    const LoadMoreIndicator = () => (
        <div className="col-span-full flex justify-center py-4">
            <svg className="animate-spin h-6 w-6 text-blue-500"
                 xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10"
                        stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
        </div>
    );

    const handleSelectConcept = (concept: Concept | null) => {
        if (!!concepts?.length) {
            setZoomConcept(concept);
        }
    };

    return (
        <div className="max-h-[calc(var(--image-row-height)*4)] overflow-y-auto pr-1 relative"
             style={{ '--image-row-height': 'min(calc((100vw - 2rem)/2), 150px)', scrollbarWidth: 'thin', scrollbarColor: '#CBD5E0 #F7FAFC' } as React.CSSProperties}>

            {/* Sorting overlay */}
            {isSorting && displayedConcepts.length > 0 && !isLoading && (
                <SortingOverlay />
            )}

            <motion.div
                className={`${!zoomConcept ? "grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4" : ""}`}
                layout="position"
                transition={{
                    layout: {
                        type: "spring",
                        stiffness: 120,
                        damping: 30,
                        duration: 1.2
                    }
                }}
            >
                {isLoading ? (
                    <LoadingSkeleton />
                ) : displayedConcepts.length > 0 && !zoomConcept ? (
                    <AnimatePresence mode="popLayout" initial={false}>
                        {displayedConcepts.map((concept) => (
                            <motion.div
                                key={concept.id}
                                className="relative rounded-lg overflow-hidden border group border-gray-300"
                                layout
                                initial={{ opacity: 0, scale: 0.9 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.9 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 150,
                                    damping: 25,
                                    duration: 1.0
                                }}
                            >
                                <div className="aspect-square relative">
                                    <img
                                        src={concept.imageUrl}
                                        alt={concept.name || 'Concept image'}
                                        className="w-full h-full object-cover transition-transform duration-200 group-hover:scale-105"
                                        onError={(e) => {
                                            const imgElement = e.currentTarget;
                                            imgElement.onerror = null;
                                            imgElement.src = `https://placehold.co/300x300?text=${concept.id.substring(0, 8)}`;
                                        }}
                                    />
                                    <button
                                        onClick={() => handleSelectConcept(concept)}
                                        className="absolute bottom-1 right-1"
                                    >
                                        <i className="">
                                            <AppIcon name="zoom_ico" size={32} />
                                        </i>
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </AnimatePresence>
                ) : zoomConcept ? (
                    <div className="aspect-square relative">
                        <img
                            src={zoomConcept?.imageUrl}
                            alt={zoomConcept?.name}
                            className="w-full h-full rounded-xl object-cover transition-transform duration-200 group-hover:scale-105"
                            onError={(e) => {
                                const imgElement = e.currentTarget;
                                imgElement.onerror = null;
                                imgElement.src = `https://placehold.co/300x300?text=${zoomConcept.id.substring(0, 8)}`;
                            }}
                        />
                        <button
                            onClick={() => setZoomConcept(null)}
                            className="absolute top-2 right-2"
                        >
                            <i className="">
                                <AppIcon name="close_ico" size={32} />
                            </i>
                        </button>
                    </div>
                ) : (
                    <EmptyState />
                )}

                {hasMore && displayedConcepts.length > 0 && !zoomConcept && !isLoading && !isSorting && (
                    <>
                        {isLoadingMore && <LoadMoreIndicator />}
                        <div ref={observerRef} className="col-span-full h-1" />
                    </>
                )}
            </motion.div>
        </div>
    );
};

export default ConceptGrid;